import { useState, useEffect, ReactNode } from 'react'
import { PublicClientApplication, type AccountInfo, type AuthenticationResult, EventType } from '@azure/msal-browser'
import type { User } from '../types'
import { loginRequest, msalConfig } from '../config/authConfig'
import { AuthContext } from './auth'

const msalInstance = new PublicClientApplication(msalConfig)

const env = import.meta.env as Record<string, string | undefined>

const isEntraConfigured = () => {
  return Boolean(env.VITE_ENTRA_CLIENT_ID?.trim() && env.VITE_ENTRA_TENANT_ID?.trim())
}

const getRolesFromClaims = (claims: unknown): string[] => {
  if (!claims || typeof claims !== 'object') return []
  const roles = (claims as Record<string, unknown>).roles
  if (!Array.isArray(roles)) return []
  return roles.filter((role): role is string => typeof role === 'string' && role.trim().length > 0)
}

const toUser = (account: AccountInfo | null, claims?: unknown): User | null => {
  if (!account) return null
  return {
    username: account.username,
    name: account.name || account.username,
    roles: getRolesFromClaims(claims),
  }
}

const shouldHandleRedirectResponse = () => {
  const { hash, search } = window.location
  if (hash && hash.length > 1) return true
  return /[?&](code|error|state)=/i.test(search)
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [ready, setReady] = useState(false)

  useEffect(() => {
    let isActive = true

    const syncSession = async () => {
      if (!isEntraConfigured()) return

      try {
        if (typeof (msalInstance as unknown as { initialize?: () => Promise<void> }).initialize === 'function') {
          await (msalInstance as unknown as { initialize: () => Promise<void> }).initialize()
        }

        let result: AuthenticationResult | null = null
        if (shouldHandleRedirectResponse()) {
          try {
            result = await msalInstance.handleRedirectPromise()
          } catch (error) {
            const errorCode =
              error && typeof error === 'object' && 'errorCode' in error && typeof (error as { errorCode: unknown }).errorCode === 'string'
                ? (error as { errorCode: string }).errorCode
                : ''
            if (errorCode !== 'hash_empty_error') {
              throw error
            }
          }
        }
        const accountFromRedirect = result?.account ?? null
        const cachedAccount = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? null
        const account = accountFromRedirect ?? cachedAccount

        if (account) {
          msalInstance.setActiveAccount(account)
        }

        if (isActive) {
          setUser(toUser(account, result?.idTokenClaims))
        }
      } catch {
        if (isActive) {
          setUser(null)
        }
      }
      if (isActive) {
        setReady(true)
      }
    }

    void syncSession()

    const callbackId = msalInstance.addEventCallback((event) => {
      if (event.eventType === EventType.LOGIN_SUCCESS || event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) {
        const authResult = event.payload as AuthenticationResult
        msalInstance.setActiveAccount(authResult.account)
        setUser(toUser(authResult.account ?? null, authResult.idTokenClaims))
      }
      if (event.eventType === EventType.LOGOUT_SUCCESS) {
        setUser(null)
      }
    })

    return () => {
      isActive = false
      if (typeof callbackId === 'string') {
        msalInstance.removeEventCallback(callbackId)
      }
    }
  }, [])

  const login = async (): Promise<boolean> => {
    if (!isEntraConfigured()) return false

    if (typeof (msalInstance as unknown as { initialize?: () => Promise<void> }).initialize === 'function') {
      await (msalInstance as unknown as { initialize: () => Promise<void> }).initialize()
    }

    const result = await msalInstance.loginPopup(loginRequest)
    if (result.account) {
      msalInstance.setActiveAccount(result.account)
    }

    setUser(toUser(result.account ?? null, result.idTokenClaims))
    return true
  }

  const logout = () => {
    const account = msalInstance.getActiveAccount() ?? undefined
    msalInstance.setActiveAccount(null)
    setUser(null)

    void (async () => {
      if (typeof (msalInstance as unknown as { initialize?: () => Promise<void> }).initialize === 'function') {
        await (msalInstance as unknown as { initialize: () => Promise<void> }).initialize()
      }

      await msalInstance.logoutPopup({
        account,
        postLogoutRedirectUri: window.location.origin,
        mainWindowRedirectUri: `${window.location.origin}/login`,
      })

      window.location.assign('/login')
    })()
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isReady: ready,
        login,
        logout
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
